import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import baseURL from '../baseURL';
import GirviPageShell from '../components/common/GirviPageShell';
import { fetchPrinterSettings, getPaperSizeCSS, getPledgeBookFontSize } from '../utils/printUtility';

const PledgeBook = () => {
  const [pledges, setPledges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedPledge, setSelectedPledge] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [pledgeBookPrintSettings, setPledgeBookPrintSettings] = useState(null);

  // Fetch user profile on mount
  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const profileRes = await fetch(baseURL + '/auth/profile', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const profile = await profileRes.json();
        setUser(profile.data?.user || null);
      } catch {
        setUser(null);
      }
    };
    fetchUser();
  }, []);

  // Set default date range to current month
  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setFromDate(firstDay.toISOString().split('T')[0]);
    setToDate(lastDay.toISOString().split('T')[0]);
  }, []);

  // Fetch printer settings for Pledge Book (A4 / A3 / Custom) to apply page size and font when printing
  useEffect(() => {
    fetchPrinterSettings().then((settings) => {
      const pb = settings?.documentSettings?.pledgeBook;
      setPledgeBookPrintSettings(pb || { type: 'STANDARD', standardSize: 'A4' });
    }).catch(() => setPledgeBookPrintSettings({ type: 'STANDARD', standardSize: 'A4' }));
  }, []);

  const fetchPledges = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      
      const res = await fetch(`${baseURL}/pledges/pledge-book`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      
      const data = await res.json();
      
      setPledges(data.data || []);
    } catch (err) {
      setError('Failed to fetch pledges.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPledges();
  }, []); // Fetch once on mount

  const handleViewDetails = (pledge) => {
    setSelectedPledge(pledge);
    setDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CREATED': return 'primary';
      case 'RECEIPT_CREATED': return 'warning';
      case 'PAID': return 'success';
      case 'RELEASED': return 'info';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'CREATED': return 'Active';
      case 'RECEIPT_CREATED': return 'Payment Made';
      case 'PAID': return 'Fully Paid';
      case 'RELEASED': return 'Released';
      default: return status;
    }
  };

  const formatCurrency = (amount) => {
    return amount ? amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return d.toLocaleDateString('en-GB');
  };

  const calculateTotalValue = (articles) => {
    return articles.reduce((sum, article) => sum + (article.valueTotal || 0), 0);
  };

  const calculateTotalWeight = (articles) => {
    return articles.reduce((sum, article) => sum + (article.grossWeight || 0), 0);
  };

  const getPaymentHistory = (pledge) => {
    // If pledge is released, all money has been received
    if (pledge.status === 'RELEASED') {
      const principalAmount = pledge.loanDetails.principalAmount || 0;
      const rate = parseFloat(pledge.loanDetails.rateOfInterest) || 0;
      const totalInterest = (principalAmount * rate / 100) * (pledge.loanDetails.duration || 12) / 12;
      const totalAmount = principalAmount + totalInterest;
      return `₹${formatCurrency(totalAmount)} received (Released)`;
    }
    
    // For active pledges, show payment history
    if (!pledge.receipts || pledge.receipts.length === 0) {
      return 'No payments made';
    }
    
    const totalPaid = pledge.totalPaid || 0;
    const principalAmount = pledge.loanDetails.principalAmount || 0;
    const remaining = principalAmount - totalPaid;
    
    return `₹${formatCurrency(totalPaid)} paid, ₹${formatCurrency(remaining)} remaining`;
  };

  const filteredPledges = pledges.filter(pledge => {
    // Date range filter
    if (fromDate || toDate) {
      const pledgeDate = new Date(pledge.date);
      if (fromDate && pledgeDate < new Date(fromDate)) {
        return false;
      }
      if (toDate && pledgeDate > new Date(toDate)) {
        return false;
      }
    }

    // Status filter
    if (statusFilter !== 'ALL' && pledge.status !== statusFilter) {
      return false;
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const customerName = `${pledge.customer.firstName} ${pledge.customer.lastName}`.toLowerCase();
      const ticketNo = pledge.loanDetails.ticketNo.toLowerCase();
      const address = pledge.customer.address.toLowerCase();
      
      return customerName.includes(searchLower) || 
             ticketNo.includes(searchLower) || 
             address.includes(searchLower);
    }
    
    return true;
  });

  const printSettings = pledgeBookPrintSettings || { type: 'STANDARD', standardSize: 'A4' };
  const printPageCSS = getPaperSizeCSS(printSettings, 'REGULAR', 'pledgeBook');
  const printFontSize = getPledgeBookFontSize(printSettings);

  return (
    <>
      {pledgeBookPrintSettings != null && (
        <style>{`@media print {
          ${printPageCSS}
          html, body { width: 100% !important; height: auto !important; margin: 0 !important; padding: 0 !important; }
          body .MuiContainer-root { max-width: none !important; width: 100% !important; margin: 0 !important; padding: 0 1cm !important; }
          .pledge-book-print { width: 100% !important; max-width: none !important; font-size: ${printFontSize}px !important; box-shadow: none !important; }
          .pledge-book-print table { font-size: inherit; width: 100% !important; table-layout: auto; }
          .pledge-book-print .MuiTableContainer-root { max-height: none !important; overflow: visible !important; }
          .no-print { display: none !important; }
        }`}</style>
      )}
      <GirviPageShell
        title="Pledge Book"
        subtitle="Transactions · Registers"
        chipLabel="Transactions"
        maxWidth="xl"
       compact>
        <Paper elevation={0} sx={{ p: 0, boxShadow: 'none' }} className="pledge-book-print">
          {/* Header */}
          {user && (
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <h2 style={{ margin: 0 }}>{user.name}</h2>
              <div>{user.companyName}</div>
              <div>{user.address}</div>
              <div>Mobile: {user.mobileNumber}</div>
              <div>License: {user.licenseNumber}</div>
            </div>
          )}
          
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 3 }}>
            FROM DATE {formatDate(fromDate)} TO {formatDate(toDate)}
          </Typography>

          {/* Filters and print tip */}
          <Box className="no-print" sx={{ mb: 3 }}>
            {printSettings.standardSize === 'A3' && (
              <Typography variant="body2" color="info.main" sx={{ mb: 1 }}>
                Print tip: For A3, in the print dialog choose <strong>Custom size 400 × 220 mm</strong> (or 40 × 22 cm) and <strong>Landscape</strong>. Do not use &quot;A3 297×420 mm&quot;.
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="Search Customer/Ticket"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            />
            <TextField
              select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              size="small"
              sx={{ minWidth: 120 }}
            >
              <option value="ALL">All Status</option>
              <option value="CREATED">Active</option>
              <option value="RECEIPT_CREATED">Payment Made</option>
              <option value="PAID">Fully Paid</option>
              <option value="RELEASED">Released</option>
            </TextField>
            <Button
              className="no-print"
              variant="contained"
              onClick={fetchPledges}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            >
              Refresh Data
            </Button>
            <Button
              className="no-print"
              variant="outlined"
              onClick={() => window.print()}
              startIcon={<PrintIcon />}
            >
              Print
            </Button>
            </Box>
          </Box>

          {error && (
            <Box className="no-print" sx={{ mb: 2 }}>
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          {/* Summary Cards - hidden when printing */}
          <Box className="no-print" sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Pledges
                    </Typography>
                    <Typography variant="h4">
                      {filteredPledges.length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Active Pledges
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {filteredPledges.filter(p => p.status === 'CREATED').length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Principal
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      ₹{formatCurrency(filteredPledges.reduce((sum, p) => sum + (p.loanDetails.principalAmount || 0), 0))}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Released Pledges
                    </Typography>
                    <Typography variant="h4" color="info.main">
                      {filteredPledges.filter(p => p.status === 'RELEASED').length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* Pledge Book Table */}
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Ticket No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Name & Address</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Rate of Interest</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Articles Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Weight (g)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Value (₹)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Principal (₹)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Payments</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Status</TableCell>
                  <TableCell className="no-print" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPledges.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} sx={{ textAlign: 'center', fontStyle: 'italic' }}>
                      {loading ? 'Loading pledges...' : 'No pledges found for the selected criteria'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPledges.map((pledge) => (
                    <TableRow key={pledge._id} hover>
                                             <TableCell>{formatDate(pledge.date)}</TableCell>
                       <TableCell>
                         <Box
                           sx={{
                             display: 'inline-flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                             minWidth: '120px',
                             height: '32px',
                             px: 2,
                             py: 0.5,
                             borderRadius: '16px', // Oval shape
                             border: pledge.status === 'RELEASED' ? '2px solid #1976d2' : 'none',
                             backgroundColor: pledge.status === 'RELEASED' ? '#e3f2fd' : 'transparent',
                             color: pledge.status === 'RELEASED' ? '#1976d2' : 'inherit',
                             fontWeight: 'bold',
                             fontSize: '0.875rem',
                             textAlign: 'center'
                           }}
                         >
                           {pledge.loanDetails.ticketNo}
                         </Box>
                       </TableCell>
                      <TableCell>
                        <div>
                          <strong>{pledge.customer.firstName} {pledge.customer.lastName}</strong>
                          <br />
                          <small>{pledge.customer.address}</small>
                          <br />
                          <small>Mobile: {pledge.customer.mobileNo}</small>
                        </div>
                      </TableCell>
                      <TableCell>{pledge.loanDetails.rateOfInterest}%</TableCell>
                      <TableCell>
                        {pledge.articles.map((article, idx) => (
                          <div key={idx}>
                            {article.description}
                            {article.purity && ` (${article.purity})`}
                          </div>
                        ))}
                      </TableCell>
                      <TableCell>
                        {pledge.articles.map((article, idx) => (
                          <div key={idx}>
                            {article.grossWeight ?? article.grossWt}g
                            {(article.netWeight ?? article.netWt) && ` / ${article.netWeight ?? article.netWt}g`}
                          </div>
                        ))}
                      </TableCell>
                      <TableCell>
                        {pledge.articles.map((article, idx) => (
                          <div key={idx}>₹{formatCurrency(article.valueTotal)}</div>
                        ))}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        ₹{formatCurrency(pledge.loanDetails.principalAmount)}
                      </TableCell>
                      <TableCell>
                        <small>{getPaymentHistory(pledge)}</small>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusText(pledge.status)}
                          color={getStatusColor(pledge.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell className="no-print">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(pledge)}
                          title="View Details"
                        >
                          <ViewIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </GirviPageShell>

      {/* Pledge Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Pledge Details - {selectedPledge?.loanDetails?.ticketNo}
        </DialogTitle>
        <DialogContent>
          {selectedPledge && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6">Customer Information</Typography>
                  <Typography><strong>Name:</strong> {selectedPledge.customer.firstName} {selectedPledge.customer.lastName}</Typography>
                  <Typography><strong>Address:</strong> {selectedPledge.customer.address}</Typography>
                  <Typography><strong>Mobile:</strong> {selectedPledge.customer.mobileNo}</Typography>
                  <Typography><strong>Occupation:</strong> {selectedPledge.customer.occupation}</Typography>
                  <Typography><strong>Caste:</strong> {selectedPledge.customer.caste || 'N/A'}</Typography>
                  <Typography><strong>Village:</strong> {selectedPledge.customer.village}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6">Pledge Information</Typography>
                  <Typography><strong>Date:</strong> {formatDate(selectedPledge.date)}</Typography>
                  <Typography><strong>Ticket No:</strong> {selectedPledge.loanDetails.ticketNo}</Typography>
                  <Typography><strong>Principal Amount:</strong> ₹{formatCurrency(selectedPledge.loanDetails.principalAmount)}</Typography>
                  <Typography><strong>Interest Rate:</strong> {selectedPledge.loanDetails.rateOfInterest}%</Typography>
                  <Typography><strong>Dhara:</strong> {selectedPledge.loanDetails.dhara || '—'}</Typography>
                  <Typography><strong>Duration:</strong> {selectedPledge.loanDetails.duration} months</Typography>
                  <Typography><strong>Status:</strong> {getStatusText(selectedPledge.status)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6">Articles</Typography>
                  {selectedPledge.articles.map((article, idx) => (
                    <Card key={idx} sx={{ mb: 1 }}>
                      <CardContent>
                        <Typography><strong>Description:</strong> {article.description}</Typography>
                        <Typography><strong>Value:</strong> ₹{formatCurrency(article.valueTotal)}</Typography>
                        <Typography><strong>Gross Weight:</strong> {article.grossWeight}g</Typography>
                        {article.netWeight && <Typography><strong>Net Weight:</strong> {article.netWeight}g</Typography>}
                        {article.purity && <Typography><strong>Purity:</strong> {article.purity}</Typography>}
                      </CardContent>
                    </Card>
                  ))}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6">Payment History</Typography>
                  {selectedPledge.receipts && selectedPledge.receipts.length > 0 ? (
                    selectedPledge.receipts.map((receipt, idx) => (
                      <Card key={idx} sx={{ mb: 1 }}>
                        <CardContent>
                          <Typography><strong>Date:</strong> {formatDate(receipt.date)}</Typography>
                          <Typography><strong>Amount:</strong> ₹{formatCurrency(receipt.totalAmount)}</Typography>
                          <Typography><strong>Interest:</strong> ₹{formatCurrency(receipt.interestDetails.calculatedInterest)}</Typography>
                          <Typography><strong>Receipt No:</strong> {receipt.receiptNo}</Typography>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Typography color="textSecondary">No payments made yet</Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PledgeBook; 